import { Link } from 'react-router';
import { ArrowRight, BadgeCheck, Clock, Inbox, Star, Wallet, Wrench } from '@plataforma/ui/iconos';
import { useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../../formato';
import type { Monto } from '../tipos';

interface Convocatoria {
  id: string;
  folio: string;
  titulo: string;
  ciudad: string;
  cierraEn: string;
  estado: string;
  miOferta: { estado: string } | null;
}
interface Trabajo {
  id: string;
  folio: string;
  titulo: string;
  precio: Monto;
  estado: string;
  estadoPago: string;
  pagadoMxn: string;
  calificacion: { precio: number; plazo: number; calidad: number } | null;
}

const promedio = (c: NonNullable<Trabajo['calificacion']>) => (c.precio + c.plazo + c.calidad) / 3;

/**
 * Inicio del Portal de Proveedores (arquitectura 2.16, MVP4 V5): lo que hay que ofertar, lo que
 * hay que entregar y lo que falta por cobrar, con la verificación del perfil a la vista.
 */
export function InicioPortal() {
  const convocatorias = useConsulta<Convocatoria[]>('/workshop/portal-proveedor/convocatorias');
  const trabajos = useConsulta<Trabajo[]>('/workshop/portal-proveedor/trabajos');
  const perfil = useConsulta<{ verificacion: string }>('/workshop/portal-proveedor/perfil');

  const abiertas = (convocatorias.datos ?? []).filter((c) => c.estado === 'abierta');
  const sinOferta = abiertas.filter((c) => !c.miOferta);
  const enCurso = (trabajos.datos ?? []).filter(
    (t) => !['aceptado', 'cancelado'].includes(t.estado),
  );
  const porCobrar = (trabajos.datos ?? []).reduce(
    (s, t) => s + Math.max(0, Number(t.precio.equivalenteMxn) - Number(t.pagadoMxn)),
    0,
  );
  const calificados = (trabajos.datos ?? []).filter((t) => t.calificacion);
  const reputacion = calificados.length
    ? calificados.reduce(
        (s, t) => s + promedio(t.calificacion as NonNullable<Trabajo['calificacion']>),
        0,
      ) / calificados.length
    : null;
  const cargando = !convocatorias.datos || !trabajos.datos;

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Portal de proveedores</h1>
          <div className="subtitulo">Lo que te piden los loteros y lo que tienes en curso</div>
        </div>
        <div className="relleno" />
        <Link className="boton primario" to="/portal/convocatorias">
          <Inbox size={18} aria-hidden="true" /> Ver convocatorias
        </Link>
      </div>

      {perfil.estadoError === 404 && (
        <p className="aviso-caja">
          Completa <Link to="/portal/perfil">tu perfil</Link> (especialidades y zona) para recibir
          convocatorias.
        </p>
      )}
      {perfil.datos && perfil.datos.verificacion !== 'verificado' && (
        <p className="aviso-caja">
          Tu perfil está en verificación: Siniestrados revisa tus datos antes de mostrarte a los
          loteros.
        </p>
      )}

      <div className="tarjetas">
        <div className="tarjeta indicador">
          <div className="rotulo">
            <Inbox size={16} aria-hidden="true" /> Convocatorias abiertas
          </div>
          <div className="cifra">{cargando ? '…' : abiertas.length}</div>
          <div className="nota">{cargando ? ' ' : `${sinOferta.length} sin tu oferta`}</div>
        </div>
        <div className="tarjeta indicador">
          <div className="rotulo">
            <Wrench size={16} aria-hidden="true" /> Trabajos en curso
          </div>
          <div className="cifra">{cargando ? '…' : enCurso.length}</div>
          <div className="nota">Adjudicados, en proceso o entregados sin aceptar</div>
        </div>
        <div className="tarjeta indicador">
          <div className="rotulo">
            <Wallet size={16} aria-hidden="true" /> Por cobrar
          </div>
          <div className="cifra">{cargando ? '…' : mxn(porCobrar.toFixed(2))}</div>
          <div className="nota">De trabajos adjudicados</div>
        </div>
        <div className="tarjeta indicador">
          <div className="rotulo">
            <Star size={16} aria-hidden="true" /> Tu calificación
          </div>
          <div className="cifra">{reputacion ? `${reputacion.toFixed(1)} ★` : '—'}</div>
          <div className="nota">
            {calificados.length
              ? `De ${calificados.length} ${calificados.length === 1 ? 'trabajo' : 'trabajos'}`
              : 'Aún sin calificaciones'}
          </div>
        </div>
      </div>

      <section className="seccion" aria-labelledby="ofertar">
        <header>
          <h2 id="ofertar">Para ofertar</h2>
          <div className="relleno" />
          <Link className="boton chico" to="/portal/convocatorias">
            Todas
          </Link>
        </header>
        {sinOferta.length === 0 ? (
          <p className="vacio cuerpo">
            <BadgeCheck size={20} aria-hidden="true" /> No hay convocatorias abiertas sin tu oferta.
          </p>
        ) : (
          <ul className="cuerpo recientes">
            {sinOferta.slice(0, 6).map((c) => (
              <li key={c.id}>
                <Link to={`/portal/convocatorias/${c.id}`}>
                  <span>
                    <strong>{c.titulo}</strong>
                    <small className="vin">
                      {c.folio} · {c.ciudad}
                    </small>
                  </span>
                  <span className="etiqueta aviso">
                    <Clock size={12} aria-hidden="true" /> cierra {fechaCorta(c.cierraEn)}
                  </span>
                  <ArrowRight size={16} aria-hidden="true" className="pendiente-flecha" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="seccion" aria-labelledby="curso">
        <header>
          <h2 id="curso">Trabajos en curso</h2>
          <div className="relleno" />
          <Link className="boton chico" to="/portal/trabajos">
            Mis trabajos
          </Link>
        </header>
        {enCurso.length === 0 ? (
          <p className="vacio cuerpo">Sin trabajos en curso.</p>
        ) : (
          <ul className="cuerpo recientes">
            {enCurso.slice(0, 6).map((t) => (
              <li key={t.id}>
                <Link to="/portal/trabajos">
                  <span>
                    <strong>{t.titulo}</strong>
                    <small className="vin">{t.folio}</small>
                  </span>
                  <span className={`etiqueta ${t.estadoPago === 'pagado' ? 'exito' : 'aviso'}`}>
                    pago {t.estadoPago}
                  </span>
                  <small>{mxn(t.precio.equivalenteMxn)}</small>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
